import type { DashboardCase } from '@core/types/types.endpoint.dashboard';

export type DashboardMetrics = {
  totalQuestions: number;
  helpfulAnswers: number;
  unratedAnswers: number;
  unhelpfulAnswers: number;
  noAnswerFound: number;
  dmQuestions: number;
  dmSearchResultsCount: number;
  dmUniqueUsers: number;
  averageTTRAutomated?: number;
  averageTTRManual?: number;
  averageTTROverall?: number;
  totalTimeSpentAutomated?: number;
  totalTimeSpentManual?: number;
};

/**
 * Compute dashboard metrics from the accumulated case list.
 * Mirrors the previous server-side aggregation; runs cheaply on every page
 * arrival since the frontend holds a single flat list.
 */
export function computeDashboardMetrics(
  cases: DashboardCase[],
  hasDMFilter: boolean,
): DashboardMetrics {
  const metrics: DashboardMetrics = {
    totalQuestions: 0,
    helpfulAnswers: 0,
    unratedAnswers: 0,
    unhelpfulAnswers: 0,
    noAnswerFound: 0,
    dmQuestions: 0,
    dmSearchResultsCount: 0,
    dmUniqueUsers: 0,
  };

  const automatedTTRs: number[] = [];
  const manualTTRs: number[] = [];
  const allTTRs: number[] = [];
  const dmUserIds = new Set<string>();

  for (const c of cases) {
    metrics.totalQuestions++;

    if (c.is_dm) {
      if (c.question_user_id) dmUserIds.add(c.question_user_id);
      metrics.dmQuestions++;
      if ((c.auto_responder?.length ?? 0) > 0) metrics.dmSearchResultsCount++;
    }

    switch (c.classification) {
      case 'helpful':
        metrics.helpfulAnswers++;
        break;
      case 'unrated':
        metrics.unratedAnswers++;
        break;
      case 'unhelpful':
        metrics.unhelpfulAnswers++;
        break;
      case 'no_answer':
        metrics.noAnswerFound++;
        break;
    }

    if (
      c.time_to_resolution_minutes !== undefined &&
      c.time_to_resolution_minutes > 0
    ) {
      const ttr = c.time_to_resolution_minutes;
      allTTRs.push(ttr);
      if (c.classification === 'helpful' || c.classification === 'unrated') {
        automatedTTRs.push(ttr);
      } else if (
        c.classification === 'unhelpful' ||
        c.classification === 'no_answer'
      ) {
        manualTTRs.push(ttr);
      }
    }
  }

  // DM-only mode uses the DM-specific counts derived above. In non-DM modes,
  // dmQuestions/dmSearchResultsCount pick up regular cases whose channel starts
  // with "D" (preserved from the previous server-side behaviour).
  if (hasDMFilter) {
    metrics.dmUniqueUsers = dmUserIds.size;
  } else {
    metrics.dmUniqueUsers = dmUserIds.size;
  }

  if (automatedTTRs.length > 0) {
    const sum = automatedTTRs.reduce((s, v) => s + v, 0);
    metrics.averageTTRAutomated = sum / automatedTTRs.length;
  }

  if (manualTTRs.length > 0) {
    const sum = manualTTRs.reduce((s, v) => s + v, 0);
    metrics.averageTTRManual = sum / manualTTRs.length;
    metrics.totalTimeSpentManual = sum;
  }

  if (allTTRs.length > 0) {
    metrics.averageTTROverall =
      allTTRs.reduce((s, v) => s + v, 0) / allTTRs.length;
  }

  // Total time saved = (team pace × automated count) − time the agent actually spent.
  if (automatedTTRs.length > 0 && metrics.averageTTRManual !== undefined) {
    const automatedSum = automatedTTRs.reduce((s, v) => s + v, 0);
    const teamWouldHaveSpent = metrics.averageTTRManual * automatedTTRs.length;
    metrics.totalTimeSpentAutomated = teamWouldHaveSpent - automatedSum;
  }

  return metrics;
}
